import { ApiClient } from "./api.ts";

export interface Organization {
    id?: string;
    name: string;
    role?: string;
    stripe_customer_id?: string;
    stripe_subscription_id?: string;
    subscription_status?: string;
    is_paid?: boolean;
    plan_id?: string;
    pending_plan_id?: string;
    cancel_at_period_end?: boolean;
    active_modules?: string[];
}

export interface AuditLog {
    id: string;
    timestamp: string;
    action: string;
    actor_email: string;
    target_name: string;
    details: any;
}

export interface ConfigHistory {
    id: string;
    version: number;
    applied_by: string;
    created_at: string;
}

export class OrganizationService {
    private static instance: OrganizationService;
    private api = ApiClient.getInstance();

    private constructor() { }

    public static getInstance(): OrganizationService {
        if (!OrganizationService.instance) {
            OrganizationService.instance = new OrganizationService();
        }
        return OrganizationService.instance;
    }

    public async create(name: string): Promise<Organization> {
        return this.api.request<Organization>(`/organization`, {
            method: 'PUT',
            body: JSON.stringify({ name })
        }) as Promise<Organization>;
    }

    public async delete(orgId: string): Promise<void> {
        await this.api.request<void>(`/organization/${orgId}`, {
            method: 'DELETE',
            toast: { success: 'Organization deleted successfully' }
        });
    }

    public async get(id: string): Promise<Organization> {
        if (!id) throw new Error('Organization ID is required');
        return this.api.request<Organization>(`/organization/${id}`, {
            method: 'GET'
        }) as Promise<Organization>;
    }

    public async update(id: string, name: string): Promise<Organization> {
        if (!id) throw new Error('Organization ID is required');
        return this.api.request<Organization>(`/organization/${id}`, {
            method: 'PATCH',
            body: JSON.stringify({ name })
        }) as Promise<Organization>;
    }

    public async getAuditLogs(orgId: string, limit: number = 20, offset: number = 0, filters: any = {}): Promise<{ data: AuditLog[], meta: any }> {
        const query = new URLSearchParams({
            limit: limit.toString(),
            offset: offset.toString(),
            ...filters
        });
        // Remove empty filters
        for (const [key, value] of query.entries()) {
            if (!value) query.delete(key);
        }
        return this.api.request(`/organization/${orgId}/auditlogs?${query.toString()}`, {
            method: 'GET'
        }) as Promise<{ data: AuditLog[], meta: any }>;
    }

    public async exportAuditLogs(orgId: string, filters: any = {}): Promise<void> {
        const query = new URLSearchParams(filters);
        // Remove empty filters
        for (const [key, value] of query.entries()) {
            if (!value) query.delete(key);
        }
        const url = `/organization/${orgId}/auditlogs/export?${query.toString()}`;
        // We trigger a direct browser download for ease of use
        const response = await this.api.request(url, { method: 'GET' }, true);
        const blob = await (response as Response).blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `audit-logs-${orgId}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
    }

    public async getConfig(orgId: string): Promise<string> {
        const response = await this.api.request<Response>(`/organization/${orgId}/config`, {
            method: 'GET',
            headers: {
                'Accept': 'application/x-yaml'
            }
        }, true);

        if (response instanceof Response) {
            return await response.text();
        }
        return '';
    }

    public async updateConfig(orgId: string, yaml: string): Promise<void> {
        await this.api.request<void>(`/organization/${orgId}/config`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/x-yaml'
            },
            body: yaml
        });
    }

    public async getInvoices(orgId: string): Promise<any[]> {
        return this.api.request<any[]>(`/organization/${orgId}/billing/invoices`, {
            method: 'GET'
        }) as Promise<any[]>;
    }

    public async getDataUsage(orgId: string): Promise<number> {
        const query = new URLSearchParams({
            organization_id: orgId,
            metric: 'data_usage',
            // Past 30 days
            start: Math.floor((Date.now() - 30 * 24 * 60 * 60 * 1000) / 1000).toString(),
            end: Math.floor(Date.now() / 1000).toString()
        });
        const res = await this.api.request<any>(`/metrics?${query.toString()}`, {
            method: 'GET'
        });
        return res?.value || 0;
    }

    public async getMauUsage(orgId: string): Promise<number> {
        const query = new URLSearchParams({
            organization_id: orgId,
            metric: 'unique_identities',
            // Current month (since MAU resets monthly)
            start: Math.floor(new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime() / 1000).toString(),
            end: Math.floor(Date.now() / 1000).toString()
        });
        const res = await this.api.request<any>(`/metrics?${query.toString()}`, {
            method: 'GET'
        });
        return res?.value || 0;
    }

    public async getPlans(): Promise<any[]> {
        return this.api.request<any[]>(`/plans`, {
            method: 'GET'
        }) as Promise<any[]>;
    }

    public async getSubscription(orgId: string): Promise<any> {
        return this.api.request<any>(`/organization/${orgId}/billing/subscription`, {
            method: 'GET'
        }) as Promise<any>;
    }

    public async setupBilling(orgId: string, email: string, planId?: string, moduleIds?: string[]): Promise<{
        client_secret: string;
        amount: number;
        customer_id: string;
        plan_name: string;
        price_cents: number;
        is_deferred: boolean;
        stripe_pk: string;
        discount_cents?: number;
        final_amount_cents?: number;
        voucher_code?: string;
    }> {
        return this.api.request<{
            client_secret: string;
            amount: number;
            customer_id: string;
            plan_name: string;
            price_cents: number;
            is_deferred: boolean;
            stripe_pk: string;
            discount_cents?: number;
            final_amount_cents?: number;
            voucher_code?: string;
        }>(`/organization/${orgId}/billing/payment-intent`, {
            method: 'PUT', body: JSON.stringify({ email, plan_id: planId, module_ids: moduleIds })
        }) as Promise<{
            client_secret: string;
            amount: number;
            customer_id: string;
            plan_name: string;
            price_cents: number;
            is_deferred: boolean;
            stripe_pk: string;
            discount_cents?: number;
            final_amount_cents?: number;
            voucher_code?: string;
        }>;
    }

    public async confirmPayment(orgId: string, paymentIntentId: string, planId?: string, moduleIds?: string[]): Promise<any> {
        const payload: any = { payment_intent_id: paymentIntentId };
        if (planId) {
            payload.plan_id = planId;
        }
        if (moduleIds) {
            payload.module_ids = moduleIds;
        }
        return await this.api.request<any>(`/organization/${orgId}/billing/confirm-payment`, {
            method: 'POST',
            body: JSON.stringify(payload),
            toast: { success: 'Payment confirmed successfully' }
        });
    }


    public async redeemVoucher(orgId: string, code: string): Promise<void> {
        await this.api.request<void>(`/organization/${orgId}/billing/voucher/redeem`, {
            method: 'PUT',
            body: JSON.stringify({ code }),
            toast: { success: 'Voucher redeemed successfully' }
        });
    }

    public async getPaymentMethod(orgId: string): Promise<{
        brand: string;
        last4: string;
        exp_month: number;
        exp_year: number;
    } | null> {
        return this.api.request<{
            brand: string;
            last4: string;
            exp_month: number;
            exp_year: number;
        } | null>(`/organization/${orgId}/billing/payment-method`) as Promise<{
            brand: string;
            last4: string;
            exp_month: number;
            exp_year: number;
        } | null>;
    }

    public async cancelSubscription(orgId: string): Promise<void> {
        await this.api.request<void>(`/organization/${orgId}/billing/cancel-subscription`, {
            method: 'POST',
            toast: { success: 'Subscription cancelled' }
        });
    }

    public async downloadInvoice(orgId: string, invoiceId: string): Promise<void> {
        const url = `/organization/${orgId}/billing/invoices/${invoiceId}/download`;
        const response = await this.api.request<{url: string}>(url, { method: 'GET' });
        if (response && response.url) {
            window.open(response.url, '_blank');
        }
    }


    public async getAvailableModules(orgId: string): Promise<any[]> {
        return this.api.request<any[]>(`/organization/${orgId}/billing/modules/available`, {
            method: 'GET'
        }) as Promise<any[]>;
    }

    public async previewSubscriptionUpdate(orgId: string, planId?: string, moduleIds?: string[]): Promise<{ delta_cents: number, line_items: any[] }> {
        return this.api.request<{ delta_cents: number, line_items: any[] }>(`/organization/${orgId}/billing/preview`, {
            method: 'POST',
            body: JSON.stringify({ plan_id: planId, module_ids: moduleIds })
        }) as Promise<{ delta_cents: number, line_items: any[] }>;
    }

    public async updateSubscription(orgId: string, planId?: string, moduleIds?: string[]): Promise<any> {
        return await this.api.request<any>(`/organization/${orgId}/billing/subscription`, {
            method: 'POST',
            body: JSON.stringify({ plan_id: planId, module_ids: moduleIds }),
            toast: { success: 'Subscription updated successfully' }
        });
    }

    public async getPortalSession(orgId: string, returnUrl: string): Promise<{ url: string }> {
        return await this.api.request<{ url: string }>(`/organization/${orgId}/billing/portal`, {
            method: 'POST',
            body: JSON.stringify({ return_url: returnUrl })
        });
    }


    public async getConfigHistory(orgId: string, limit: number = 20, offset: number = 0): Promise<{ entries: ConfigHistory[], total: number }> {
        const query = new URLSearchParams({
            limit: limit.toString(),
            offset: offset.toString()
        });
        return this.api.request<{ entries: any[], total: number }>(`/organization/${orgId}/config/history?${query.toString()}`, {
            method: 'GET'
        }) as Promise<{ entries: any[], total: number }>;
    }

    public async getConfigHistoryEntry(orgId: string, historyId: string): Promise<any> {
        return this.api.request<any>(`/organization/${orgId}/config/history/${historyId}`, {
            method: 'GET'
        }) as Promise<any>;
    }

    public async rollbackConfig(orgId: string, historyId: string): Promise<void> {
        await this.api.request<void>(`/organization/${orgId}/config/rollback`, {
            method: 'POST',
            body: JSON.stringify({ history_id: historyId }),
            toast: { success: 'Configuration rolled back' }
        });
    }

    public async rotateKmsKey(orgId: string, system: 'secrets' | 'agent_vault' | 'identity' | 'rsa'): Promise<void> {
        await this.api.request<void>(`/organization/${orgId}/kms/rotate`, {
            method: 'POST',
            body: JSON.stringify({ keyType: system }),
            toast: { success: `Successfully rotated ${system} encryption key` }
        });
    }

    // ── Gateway Management ───────────────────────────────────

    public async getGateways(orgId: string): Promise<any[]> {
        return this.api.request<any[]>(`/organization/${orgId}/gateways`) || [];
    }

    public async createGateway(orgId: string, name: string, type?: string): Promise<any> {
        return this.api.request<any>(`/organization/${orgId}/gateway`, {
            method: 'PUT',
            body: JSON.stringify({ name, type }),
            toast: { success: 'Node provisioned successfully' }
        });
    }

    public async deleteGateway(orgId: string, gatewayId: string): Promise<void> {
        await this.api.request<void>(`/organization/${orgId}/gateways/${gatewayId}`, {
            method: 'DELETE',
            toast: { success: 'Node revoked successfully' }
        });
    }

}
